/** PWA helpers: service worker registration, update banner, install CTA. */

const INSTALL_DISMISS_KEY = '3d-viewer.installDismissed';

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstall: BeforeInstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function installDismissed(): boolean {
  try {
    return sessionStorage.getItem(INSTALL_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setInstallDismissed(): void {
  try {
    sessionStorage.setItem(INSTALL_DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

function req<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setHidden(el: HTMLElement | null, hidden: boolean): void {
  el?.classList.toggle('hidden', hidden);
}

/** iPhone or iPad — iPadOS reports itself as a Mac, hence the touch check. */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Safari fires no beforeinstallprompt, so on iOS the install banner never
 * appears and nothing tells you the viewer can be an app. Point at the share
 * sheet instead — but only while it is still a tab.
 */
function syncIosInstallHint(): void {
  setHidden(req('iosInstallHint'), !isIos() || isStandalone());
}

/**
 * Ask the browser to keep our data. Without this the app shell, the 7.6 MB
 * OpenCascade WASM and the recent-files database are all evictable; iOS clears
 * them after roughly a week of not opening the app. Chromium grants it
 * silently for installed apps, so there is no prompt to be afraid of.
 */
async function requestPersistentStorage(): Promise<void> {
  if (!navigator.storage?.persist || !navigator.storage.persisted) return;
  try {
    if (await navigator.storage.persisted()) return;
    await navigator.storage.persist();
  } catch {
    // Best effort: a refusal only means the cache may be evicted under pressure.
  }
}

function syncInstallUi(): void {
  const show =
    !isStandalone() &&
    deferredInstall !== null &&
    !installDismissed();

  setHidden(req('installBanner'), !show);
  setHidden(req('installHomeBtn'), !show);
}

function showUpdateBanner(worker: ServiceWorker): void {
  waitingWorker = worker;
  setHidden(req('updateBanner'), false);
}

function wireBannerButtons(): void {
  req<HTMLButtonElement>('updateReloadBtn')?.addEventListener('click', () => {
    waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
    // If the worker was already active (rare), still force a refresh.
    if (!waitingWorker) window.location.reload();
  });

  const install = async () => {
    if (!deferredInstall) return;
    const event = deferredInstall;
    deferredInstall = null;
    syncInstallUi();
    await event.prompt();
    await event.userChoice;
    syncInstallUi();
  };

  req<HTMLButtonElement>('installAppBtn')?.addEventListener('click', () => {
    void install();
  });
  req<HTMLButtonElement>('installHomeBtn')?.addEventListener('click', () => {
    void install();
  });
  req<HTMLButtonElement>('installDismissBtn')?.addEventListener('click', () => {
    setInstallDismissed();
    syncInstallUi();
  });
}

/** Capture the browser install prompt and wire update / install banners. */
export function initPwaUi(): void {
  wireBannerButtons();
  syncIosInstallHint();
  void requestPersistentStorage();

  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferredInstall = e as BeforeInstallPromptEvent;
    syncInstallUi();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstall = null;
    syncInstallUi();
  });

  syncInstallUi();
}

/**
 * What a manual update check came to. 'updating' means a new worker is taking
 * over and the page is about to reload itself; 'unsupported' means there is no
 * service worker to ask, which is the normal state of a dev build.
 */
export type UpdateOutcome = 'updating' | 'current' | 'unsupported' | 'failed';

/**
 * The banner only appears when the browser happens to notice a new worker.
 * This is the same thing on demand, for when you know a new build is out and
 * the tab has not caught up.
 */
export async function checkForUpdate(): Promise<UpdateOutcome> {
  if (!('serviceWorker' in navigator)) return 'unsupported';
  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return 'unsupported';
    await registration.update();
  } catch {
    return 'failed';
  }

  const pending = registration.waiting ?? registration.installing;
  if (!pending) return 'current';
  // Taking over fires controllerchange, and that reloads the page.
  if (pending.state === 'installed') {
    pending.postMessage({ type: 'SKIP_WAITING' });
  } else {
    pending.addEventListener('statechange', () => {
      if (pending.state === 'installed') pending.postMessage({ type: 'SKIP_WAITING' });
    });
  }
  return 'updating';
}

/**
 * The hammer: drop every cache and service worker, then reload. Everything
 * comes back over the network, the 7.6 MB OpenCascade WASM included, so this
 * is for a tab that is genuinely stuck on an old build.
 */
export async function resetAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }
  } catch {
    // A partial clear is still worth reloading after.
  }
  window.location.reload();
}

/** Register the service worker in production and surface update reloads. */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js')
      .then(registration => {
        if (registration.waiting) {
          showUpdateBanner(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateBanner(installing);
            }
          });
        });

        // Periodic check so long-lived PWA sessions still see updates.
        window.setInterval(() => {
          void registration.update();
        }, 60 * 60 * 1000);
      })
      .catch(err => {
        console.warn('Service worker registration failed:', err);
      });

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
